import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MATERIAL_ICON_CLOSE, MATERIAL_ICON_VIDEO } from '../../../shared/common/Button/Icon';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import VideoPlayerPopup from '../../../shared/components/VideoPlayerPopup';
import DocumentTitle from '../../../shared/components/DocumentTitle';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import {
    invalidateLocalContentCollectionProducts,
    useLocalContentCollectionProducts
} from '../../../api/local-content/collection/useLocalContentCollectionProducts';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { FilterBar } from '../../../shared/common/FilterBar';
import { LOCAL_COLLECTION_SORT_BY } from '../../../shared/common/SortByDropDown';
import NoElementsFound, {
    NO_ELEMENTS_LC_COLLECTION_TITLES,
    NO_ELEMENTS_LC_TITLES_FILTERED
} from '../../../shared/components/NoElementsFound';
import ProductList from '../../../shared/common/ProductList';
import CollectionProductElement from './CollectionProductElement';
import Pagination from '../../../shared/common/Pagination';
import FilterUtils from '../../../shared/utils/utils-filter';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchLocalContentCollectionAggregationsQuickSearch } from '../../../api/local-content/collection/fetchLocalContentCollectionAggregationsQuickSearch';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchLocalContentCollectionAggregations } from '../../../api/local-content/collection/fetchLocalContentCollectionAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { useProductFilters } from '../../../shared/filter/useProductFilters';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CopyDetailsPopup } from '../../collection/copyDetailsPopup/CopyDetailsPopup';
import { Article } from '../../../../models/Article';
import { ButtonContainer } from './ManageModal.styled';
import {
    invalidateHasRemovableCopy,
    useHasRemovableCopy
} from '../../../api/local-content/collection/useHasRemovableCopy';
import { invalidateProductCopies } from '../../../shop/cart/api/useProductCopies';
import Utils from '../../../shared/utils';
import { useDisplayTypeSearchParam } from '../../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { useRemoveLocalContentCopies } from '../api/useRemoveLocalContentCopies';
import { useAuthentication } from '../../../app-store/authentication/useAuthentication';
import { globalErrorResponse } from '../../../shared/notification/actions';
import { showNotification } from '../../../shared/notification/toastNotification';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { LANGUAGE_DE_DE } from '../../../../i18n';

const StButtonLine = styled.div.attrs({ 'data-displayname': 'HeaderButtonLine' })`
    margin-bottom: 62px;
`;

const FilterBarWithSpace = styled(FilterBar)`
    padding: 0 0 5px 0;
`;

export const LocalContentCollectionPage = () => {
    const {
        t,
        i18n: { language }
    } = useTranslation();
    const [manageProduct, setManageProduct] = useState<(ProductMetaData & Article) | null>(null);
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const authentication = useAuthentication();
    const isInPartnerContext = Utils.isInPartnerContext(authentication);

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultPageSize: 54,
        defaultSortBy: ProductSorting.TITLE_AZ
    });

    const { productFilter } = useProductFilters();

    const { display } = useDisplayTypeSearchParam();

    const { products, isLoading: isProductsLoading } = useLocalContentCollectionProducts({
        filterMap: productFilter,
        pagination: {
            page,
            pageSize,
            sortBy
        }
    });

    //region FILTERBAR_V2
    const isFiltered = FilterUtils.hasActiveFilter(productFilter);
    const visibleFilterGroups = useMemo(() => defaultVisibleFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'localContentCollectionPage',
            'aggregation',
            filters,
            expandedFilterGroups
        ],
        []
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchLocalContentCollectionAggregations(options),
        []
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options => fetchLocalContentCollectionAggregationsQuickSearch(options),
        []
    );

    //endregion

    const { mutate: removeLocalContentCopies } = useRemoveLocalContentCopies();

    const onRemoveCopies = useCallback(() => {
        if (!manageProduct?.id) {
            return;
        }
        removeLocalContentCopies(manageProduct.id, {
            onSuccess: () => {
                setManageProduct(null);
                showNotification(t('notifications.removeLocalContentCopies.success'));
                invalidateLocalContentCollectionProducts().then();
                invalidateHasRemovableCopy(manageProduct.id).then();
                invalidateProductCopies(manageProduct.id).then();
            },
            onError: () => {
                globalErrorResponse(t('notifications.removeLocalContentCopies.error'));
            }
        });
    }, [manageProduct, removeLocalContentCopies, t]);

    const { data: hasRemovableCopy } = useHasRemovableCopy(manageProduct?.id ?? null);

    const loading = isProductsLoading;

    return (
        <DocumentTitle title={t('components.documentTitle.localContentCollection')}>
            <LoadingWrapper>
                <LoadingOverlay loading={loading} />
            </LoadingWrapper>
            {manageProduct ? (
                <CopyDetailsPopup
                    onClosePopup={() => setManageProduct(null)}
                    onAddPrice={async () => {}}
                    product={manageProduct}
                    isBuyingOptionEnabled={false}
                    isLocalContentVariant={true}
                    removeCopyButton={
                        hasRemovableCopy ? (
                            <ButtonContainer>
                                <Button
                                    size={ButtonSize.SMALL}
                                    theme={ButtonTheme.DANGER}
                                    text={t('yourLocalContent.localContentCollection.removeFrom', {
                                        collectionLabelPrefix: isInPartnerContext
                                            ? t('yourLocalContent.localContentCollection.sharedCollection')
                                            : t('yourLocalContent.localContentCollection.individualCollection')
                                    })}
                                    icon={MATERIAL_ICON_CLOSE}
                                    onClick={onRemoveCopies}
                                />
                            </ButtonContainer>
                        ) : null
                    }
                    shopLanguage={undefined}
                />
            ) : null}
            <VideoPlayerPopup
                showVideo={showIntroVideo && language !== LANGUAGE_DE_DE}
                hideVideo={() => setShowIntroVideo(false)}
                vimeoId={272097594}
            />
            <ContainerInnerWithColumns>
                {language !== LANGUAGE_DE_DE ? (
                    <StButtonLine>
                        <Button
                            text={t('yourLocalContent.localContentCollection.introductionAndTraining')}
                            icon={MATERIAL_ICON_VIDEO}
                            onClick={() => setShowIntroVideo(true)}
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.POSITIVE_INVERT}
                        />
                    </StButtonLine>
                ) : null}
                {products.list.length === 0 ? null : (
                    <FilterBarWithSpace
                        totalProducts={products.count}
                        sorting={LOCAL_COLLECTION_SORT_BY}
                        hideGroupBy={true}
                        hideSelectButtons={true}
                        displayType={display}
                        pageSize={pageSize}
                        loading={loading}
                        sortBy={sortBy}
                    />
                )}
                <div className="content-area-inner flex-ca">
                    {displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
                        />
                    ) : null}
                    {products.list.length === 0 && !loading ? (
                        <NoElementsFound
                            type={isFiltered ? NO_ELEMENTS_LC_TITLES_FILTERED : NO_ELEMENTS_LC_COLLECTION_TITLES}
                        />
                    ) : (
                        <div className={classNames({ 'min-h-screen': isProductsLoading }, 'ca-main')}>
                            <div className="ca-main-inner">
                                <ProductList displayType={display} products={products.list} onAddPrice={async () => {}}>
                                    {({ key, isListView, ...value }) => (
                                        <CollectionProductElement
                                            {...value}
                                            product={
                                                value.product as ProductMetaData &
                                                    Article & { fromLibXchange: boolean; isbn13: string }
                                            }
                                            key={key}
                                            displayType={display}
                                            onManageButtonClick={() => setManageProduct(value.product)}
                                        />
                                    )}
                                </ProductList>
                                <Pagination totalItems={products.count} pageSize={pageSize} />
                            </div>
                        </div>
                    )}
                </div>
            </ContainerInnerWithColumns>
        </DocumentTitle>
    );
};
